import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Bell, Mail, Clock, AlertTriangle, CheckCircle2, XCircle, Plus,
  Play, Square, RefreshCw, Calendar, Package, Wrench, Eye,
  Send, Settings, History
} from 'lucide-react';

const AlertsPage = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    enabled: false,
    revision_alerts_enabled: false,
    warranty_alerts_enabled: false,
    low_stock_enabled: false,
    revision_days_ahead: 7,
    warranty_days_ahead: 30,
    email_frequency_hours: 24,
    recipients: []
  });
  const [previews, setPreviews] = useState({
    revisions: null,
    warranties: null,
    lowStock: null
  });
  const [testingAlert, setTestingAlert] = useState(null);
  const [newRecipient, setNewRecipient] = useState({ name: '', email: '', phone: '', notify_email: true, notify_sms: false });

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/alerts/status');
      setStatus(response.data);
      if (response.data.settings) {
        setSettings(response.data.settings);
      }
    } catch (error) {
      toast.error('Eroare la incarcarea statusului alertelor');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);


  const handleSaveSettings = async () => {
    try {
      await api.post('/alerts/settings', settings);
      toast.success('Setari salvate cu succes!');
    } catch (error) {
      toast.error('Eroare la salvarea setarilor');
    }
  };

  const addRecipient = () => {
    if (!newRecipient.name || (!newRecipient.email && !newRecipient.phone)) {
      toast.error('Completati numele si cel putin email sau telefon');
      return;
    }
    setSettings(prev => ({ ...prev, recipients: [...prev.recipients, { ...newRecipient }] }));
    setNewRecipient({ name: '', email: '', phone: '', notify_email: true, notify_sms: false });
  };

  const removeRecipient = (idx) => {
    setSettings(prev => ({ ...prev, recipients: prev.recipients.filter((_, i) => i !== idx) }));
  };

  const handleStartScheduler = async () => {
    try {
      const response = await api.post('/alerts/scheduler/start');
      toast.success(response.data.message);
      fetchStatus();
    } catch (error) {
      toast.error('Eroare la pornirea scheduler-ului');
    }
  };

  const handleStopScheduler = async () => {
    try {
      const response = await api.post('/alerts/scheduler/stop');
      toast.success(response.data.message);
      fetchStatus();
    } catch (error) {
      toast.error('Eroare la oprirea scheduler-ului');
    }
  };

  const handleTestAlert = async (type) => {
    setTestingAlert(type);
    try {
      let response;
      switch (type) {
        case 'revisions':
          response = await api.post(`/alerts/test/revisions?days_ahead=${settings.revision_days_ahead}`);
          break;
        case 'warranties':
          response = await api.post(`/alerts/test/warranties?days_ahead=${settings.warranty_days_ahead}`);
          break;
        case 'low-stock':
          response = await api.post('/alerts/test/low-stock');
          break;
        case 'all':
          response = await api.post('/alerts/test/all');
          break;
        default:
          return;
      }
      
      if (response.data.success || response.data.sent) {
        toast.success('Email de alerta trimis cu succes!');
      } else {
        toast.info(response.data.reason || response.data.message || 'Nu s-au gasit elemente pentru alerta');
      }
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la trimiterea alertei');
    } finally {
      setTestingAlert(null);
    }
  };

  const handlePreview = async (type) => {
    try {
      let response;
      switch (type) {
        case 'revisions':
          response = await api.get(`/alerts/preview/revisions?days_ahead=${settings.revision_days_ahead}`);
          setPreviews(prev => ({ ...prev, revisions: response.data }));
          break;
        case 'warranties':
          response = await api.get(`/alerts/preview/warranties?days_ahead=${settings.warranty_days_ahead}`);
          setPreviews(prev => ({ ...prev, warranties: response.data }));
          break;
        case 'lowStock':
          response = await api.get('/alerts/preview/low-stock');
          setPreviews(prev => ({ ...prev, lowStock: response.data }));
          break;
      }
    } catch (error) {
      toast.error('Eroare la incarcarea previzualizarii');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900">Alerte Email Automate</h1>
          <p className="text-slate-500">Gestionati notificarile automate pentru revizii, garantii si stoc</p>
        </div>
        <Button onClick={fetchStatus} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Reincarca
        </Button>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Scheduler Status */}
        <Card className={status?.scheduler?.running ? 'border-green-200 bg-green-50' : 'border-slate-200'}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  status?.scheduler?.running ? 'bg-green-100' : 'bg-slate-100'
                }`}>
                  <Clock className={`w-5 h-5 ${status?.scheduler?.running ? 'text-green-600' : 'text-slate-400'}`} />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Scheduler</p>
                  <p className="text-sm text-slate-500">
                    {status?.scheduler?.running ? 'Activ' : 'Oprit'}
                  </p>
                </div>
              </div>
              {status?.scheduler?.running ? (
                <Button size="sm" variant="outline" onClick={handleStopScheduler}>
                  <Square className="w-4 h-4 mr-1" />
                  Opreste
                </Button>
              ) : (
                <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={handleStartScheduler}>
                  <Play className="w-4 h-4 mr-1" />
                  Porneste
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Email Config Status */}
        <Card className={status?.email_configured ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                status?.email_configured ? 'bg-green-100' : 'bg-red-100'
              }`}>
                <Mail className={`w-5 h-5 ${status?.email_configured ? 'text-green-600' : 'text-red-600'}`} />
              </div>
              <div>
                <p className="font-semibold text-slate-900">Configuratie Email</p>
                <p className={`text-sm ${status?.email_configured ? 'text-green-600' : 'text-red-600'}`}>
                  {status?.email_configured ? 'Configurata' : 'Neconfigurata'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Last Alert */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <History className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">Ultima Alerta</p>
                <p className="text-sm text-slate-500">
                  {status?.last_alerts?.[0] 
                    ? new Date(status.last_alerts[0].sent_at).toLocaleString('ro-RO')
                    : 'Niciuna'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="settings" className="space-y-4">
        <TabsList>
          <TabsTrigger value="settings">
            <Settings className="w-4 h-4 mr-2" />
            Setari
          </TabsTrigger>
          <TabsTrigger value="preview">
            <Eye className="w-4 h-4 mr-2" />
            Previzualizare
          </TabsTrigger>
          <TabsTrigger value="history">
            <History className="w-4 h-4 mr-2" />
            Istoric
          </TabsTrigger>
        </TabsList>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          {/* Master Switch */}
          <Card className={settings.enabled ? 'border-green-200 bg-green-50/30' : 'border-red-200 bg-red-50/30'}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Bell className={`w-6 h-6 ${settings.enabled ? 'text-green-600' : 'text-red-500'}`} />
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">Sistem Notificari Automate</h3>
                    <p className="text-sm text-slate-500">Activeaza/dezactiveaza toate notificarile email automate</p>
                  </div>
                </div>
                <Switch
                  checked={settings.enabled}
                  onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
                  data-testid="master-alerts-switch"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Tipuri de Alerte</CardTitle>
              <CardDescription>Activati/dezactivati individual fiecare tip de notificare</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Revision Alert Settings */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Wrench className="w-5 h-5 text-orange-500" />
                    <h3 className="font-semibold">Alerte Revizii Scule</h3>
                  </div>
                  <Switch
                    checked={settings.revision_alerts_enabled}
                    onCheckedChange={(checked) => setSettings({ ...settings, revision_alerts_enabled: checked })}
                    disabled={!settings.enabled}
                    data-testid="revision-alerts-switch"
                  />
                </div>
                {settings.revision_alerts_enabled && settings.enabled && (
                  <div className="grid grid-cols-2 gap-4 pl-7">
                    <div className="space-y-2">
                      <Label>Zile in avans pentru alerta</Label>
                      <Input
                        type="number"
                        min="1"
                        max="30"
                        value={settings.revision_days_ahead}
                        onChange={(e) => setSettings({ ...settings, revision_days_ahead: parseInt(e.target.value) || 7 })}
                      />
                    </div>
                    <div className="flex items-end">
                      <Button variant="outline" onClick={() => handleTestAlert('revisions')} disabled={testingAlert === 'revisions' || !status?.email_configured}>
                        <Send className="w-4 h-4 mr-2" />Trimite Test
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <Separator />

              {/* Warranty Alert Settings */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-green-500" />
                    <h3 className="font-semibold">Alerte Garantii Echipamente</h3>
                  </div>
                  <Switch
                    checked={settings.warranty_alerts_enabled}
                    onCheckedChange={(checked) => setSettings({ ...settings, warranty_alerts_enabled: checked })}
                    disabled={!settings.enabled}
                    data-testid="warranty-alerts-switch"
                  />
                </div>
                {settings.warranty_alerts_enabled && settings.enabled && (
                  <div className="grid grid-cols-2 gap-4 pl-7">
                    <div className="space-y-2">
                      <Label>Zile in avans pentru alerta</Label>
                      <Input
                        type="number"
                        min="1"
                        max="90"
                        value={settings.warranty_days_ahead}
                        onChange={(e) => setSettings({ ...settings, warranty_days_ahead: parseInt(e.target.value) || 30 })}
                      />
                    </div>
                    <div className="flex items-end">
                      <Button variant="outline" onClick={() => handleTestAlert('warranties')} disabled={testingAlert === 'warranties' || !status?.email_configured}>
                        <Send className="w-4 h-4 mr-2" />Trimite Test
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <Separator />

              {/* Low Stock Alert Settings */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-red-500" />
                    <h3 className="font-semibold">Alerte Stoc Minim</h3>
                  </div>
                  <Switch
                    checked={settings.low_stock_enabled}
                    onCheckedChange={(checked) => setSettings({ ...settings, low_stock_enabled: checked })}
                    disabled={!settings.enabled}
                    data-testid="stock-alerts-switch"
                  />
                </div>
                {settings.low_stock_enabled && settings.enabled && (
                  <div className="pl-7">
                    <Button variant="outline" onClick={() => handleTestAlert('low-stock')} disabled={testingAlert === 'low-stock' || !status?.email_configured}>
                      <Send className="w-4 h-4 mr-2" />Trimite Test
                    </Button>
                  </div>
                )}
              </div>

              <Separator />

              {/* Frequency Settings */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-500" />
                  <h3 className="font-semibold">Frecventa Verificare</h3>
                </div>
                <div className="grid grid-cols-2 gap-4 pl-7">
                  <div className="space-y-2">
                    <Label>Interval verificare (ore)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="168"
                      value={settings.email_frequency_hours}
                      onChange={(e) => setSettings({ ...settings, email_frequency_hours: parseInt(e.target.value) || 24 })}
                    />
                    <p className="text-xs text-slate-500">Cat de des sa verifice si sa trimita alerte</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={handleSaveSettings} className="bg-blue-600 hover:bg-blue-700" data-testid="save-alert-settings">
                  Salveaza Setarile
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Recipients Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-indigo-600" />
                Destinatari Notificari
              </CardTitle>
              <CardDescription>Persoanele care vor primi notificari prin email sau SMS</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add recipient form */}
              <div className="grid grid-cols-5 gap-3 items-end p-4 bg-slate-50 rounded-lg">
                <div className="space-y-1">
                  <Label className="text-xs">Nume *</Label>
                  <Input value={newRecipient.name} onChange={e => setNewRecipient({...newRecipient, name: e.target.value})} placeholder="Nume complet" className="h-9" data-testid="recipient-name" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Email</Label>
                  <Input value={newRecipient.email} onChange={e => setNewRecipient({...newRecipient, email: e.target.value})} placeholder="email@company.ro" className="h-9" data-testid="recipient-email" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Telefon</Label>
                  <Input value={newRecipient.phone} onChange={e => setNewRecipient({...newRecipient, phone: e.target.value})} placeholder="07xx..." className="h-9" data-testid="recipient-phone" />
                </div>
                <div className="flex items-center gap-4 pt-3">
                  <label className="flex items-center gap-1.5 text-xs">
                    <input type="checkbox" checked={newRecipient.notify_email} onChange={e => setNewRecipient({...newRecipient, notify_email: e.target.checked})} className="rounded" />
                    Email
                  </label>
                  <label className="flex items-center gap-1.5 text-xs">
                    <input type="checkbox" checked={newRecipient.notify_sms} onChange={e => setNewRecipient({...newRecipient, notify_sms: e.target.checked})} className="rounded" />
                    SMS
                  </label>
                </div>
                <Button onClick={addRecipient} size="sm" className="h-9 bg-blue-600 hover:bg-blue-700" data-testid="add-recipient-btn">
                  <Plus className="w-4 h-4 mr-1" />Adauga
                </Button>
              </div>

              {/* Recipients list */}
              {settings.recipients?.length > 0 ? (
                <div className="space-y-2">
                  {settings.recipients.map((r, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50" data-testid={`recipient-${idx}`}>
                      <div className="flex items-center gap-4">
                        <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-sm">
                          {r.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{r.name}</p>
                          <div className="flex items-center gap-3 text-xs text-slate-500">
                            {r.email && <span>{r.email}</span>}
                            {r.phone && <span>{r.phone}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {r.notify_email && <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">Email</Badge>}
                        {r.notify_sms && <Badge variant="outline" className="text-xs bg-green-50 text-green-700">SMS</Badge>}
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => removeRecipient(idx)} data-testid={`remove-recipient-${idx}`}>
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-slate-400">
                  <Mail className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p>Nu exista destinatari configurati</p>
                  <p className="text-xs">Adaugati persoanele care trebuie sa primeasca notificari</p>
                </div>
              )}

              <div className="flex justify-end pt-2">
                <Button onClick={handleSaveSettings} className="bg-blue-600 hover:bg-blue-700">
                  Salveaza Destinatari
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Preview Tab */}
        <TabsContent value="preview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Revisions Preview */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-orange-500" />
                  Revizii in curs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Button variant="outline" size="sm" className="w-full mb-3" onClick={() => handlePreview('revisions')}>
                  <Eye className="w-4 h-4 mr-2" />
                  Incarca previzualizare
                </Button>
                {previews.revisions && (
                  <div className="space-y-2">
                    <Badge className="bg-orange-100 text-orange-700">{previews.revisions.count} scule</Badge>
                    <div className="max-h-[200px] overflow-y-auto space-y-2">
                      {previews.revisions.items?.slice(0, 5).map((item, idx) => (
                        <div key={idx} className="text-sm p-2 bg-slate-50 rounded">
                          <p className="font-medium">{item.code}</p>
                          <p className="text-slate-500">{item.next_revision_date}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Warranties Preview */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-green-500" />
                  Garantii in expirare
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Button variant="outline" size="sm" className="w-full mb-3" onClick={() => handlePreview('warranties')}>
                  <Eye className="w-4 h-4 mr-2" />
                  Incarca previzualizare
                </Button>
                {previews.warranties && (
                  <div className="space-y-2">
                    <Badge className="bg-green-100 text-green-700">{previews.warranties.count} echipamente</Badge>
                    <div className="max-h-[200px] overflow-y-auto space-y-2">
                      {previews.warranties.items?.slice(0, 5).map((item, idx) => (
                        <div key={idx} className="text-sm p-2 bg-slate-50 rounded">
                          <p className="font-medium">{item.code}</p>
                          <p className="text-slate-500">{item.warranty_expiry}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Low Stock Preview */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Package className="w-5 h-5 text-red-500" />
                  Stoc sub minim
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Button variant="outline" size="sm" className="w-full mb-3" onClick={() => handlePreview('lowStock')}>
                  <Eye className="w-4 h-4 mr-2" />
                  Incarca previzualizare
                </Button>
                {previews.lowStock && (
                  <div className="space-y-2">
                    <Badge className="bg-red-100 text-red-700">{previews.lowStock.count} articole</Badge>
                    <div className="max-h-[200px] overflow-y-auto space-y-2">
                      {previews.lowStock.items?.slice(0, 5).map((item, idx) => (
                        <div key={idx} className="text-sm p-2 bg-slate-50 rounded">
                          <p className="font-medium">{item.name}</p>
                          <p className="text-slate-500">{item.current_stock} / {item.min_stock} {item.unit}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Istoric Alerte Trimise</CardTitle>
            </CardHeader>
            <CardContent>
              {status?.last_alerts?.length > 0 ? (
                <div className="space-y-3">
                  {status.last_alerts.map((alert, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          alert.success ? 'bg-green-100' : 'bg-red-100'
                        }`}>
                          {alert.success ? (
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            {alert.type === 'revision_expiry' && 'Alerte Revizii'}
                            {alert.type === 'warranty_expiry' && 'Alerte Garantii'}
                            {alert.type === 'low_stock' && 'Alerte Stoc Minim'}
                          </p>
                          <p className="text-sm text-slate-500">
                            {new Date(alert.sent_at).toLocaleString('ro-RO')} • {alert.items_count} elemente
                          </p>
                        </div>
                      </div>
                      <Badge variant={alert.success ? 'default' : 'destructive'}>
                        {alert.success ? 'Trimis' : 'Esuat'}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Bell className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p>Nu exista alerte in istoric</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AlertsPage;
